import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { io } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { makeRequest } from "../../../../utils/makeRequest"
import { getCustomerPhoneNumberURL } from "../../../../utils/urls";
import axios from 'axios';

const PaymentModal = ({
    visible,
    onHide,
    accountNumber,
    facilityId,
    customerId,
    amount,
    onSuccess,
    onError,
    phoneNumber
}) => {
    // Configuration variables - FIXED with fallback values
    const PAYMENT_API_BASE_URL = process.env.REACT_APP_PAYMENT_API_URL || "https://sandbox.payments.payserve.co.ke";
    const SOCKET_URL = process.env.REACT_APP_PAYMENT_API_URL || "https://sandbox.payments.payserve.co.ke";

    // In development environment, override the customer API base URL
    const CUSTOMER_API_BASE_URL = process.env.NODE_ENV === 'development'
        ? 'http://localhost:3050'
        : 'https://api.payserve.co.ke';

    const toast = useRef(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [socket, setSocket] = useState(null);
    const [paymentFormData, setPaymentFormData] = useState({
        phone: formatInitialPhoneNumber(phoneNumber) || '',
        amount: amount || ''
    });
    const uniqueIdentifier = useRef(uuidv4());
    const pingInterval = useRef(null);
    const connectionTimeout = useRef(null);
    const [facilityData, setFacilityData] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    // Extract proper facility ID from various possible formats
    const extractFacilityId = (facilityId) => {
        if (!facilityId) return null;

        // Handle case where facilityId is an object with $oid property (from MongoDB)
        if (typeof facilityId === 'object' && facilityId.$oid) {
            return facilityId.$oid;
        }

        // Handle case where facilityId is an object with id property
        if (typeof facilityId === 'object' && facilityId.id) {
            // Handle nested $oid
            if (typeof facilityId.id === 'object' && facilityId.id.$oid) {
                return facilityId.id.$oid;
            }
            return facilityId.id;
        }

        // Handle case where facility is a string
        return facilityId;
    };

    // Get the actual IDs we'll use for API calls
    const actualFacilityId = extractFacilityId(facilityId);

    // Handle customerId directly 
    const actualCustomerId = customerId;

    // Format initial phone number if provided. Kenyan subscriber numbers are
    // always 9 significant digits (e.g. 7XXXXXXXX) regardless of how they
    // arrive - with a leading 0, a 254 prefix, a doubled prefix, a stray "+",
    // etc. Taking the last 9 digits and re-prepending 254 is idempotent and
    // self-corrects malformed/double-prefixed input instead of relying on
    // fragile prefix-stripping that silently drops a digit when the input
    // doesn't match the exact shape it expects.
    function formatInitialPhoneNumber(phone) {
        if (!phone) return '';

        const digits = phone.replace(/\D/g, '');
        const significant = digits.slice(-9);

        return '254' + significant;
    }

    // Update form data when props change
    useEffect(() => {
        const updates = {};
        if (amount) updates.amount = amount;
        if (phoneNumber) updates.phone = formatInitialPhoneNumber(phoneNumber);

        if (Object.keys(updates).length > 0) {
            setPaymentFormData(prev => ({
                ...prev,
                ...updates
            }));
        }
    }, [amount, phoneNumber]);

    // Fetch facility payment details when modal becomes visible
    useEffect(() => {
        if (visible && actualFacilityId) {
            fetchFacilityPaymentDetails();
        }
    }, [visible, actualFacilityId]);

    // Initialize socket connection when modal becomes visible
    useEffect(() => {
        if (visible) {
            // Reset status and error when reopening the modal
            setStatus(null);
            setErrorMessage('');

            // Initialize the socket connection
            initSocketConnection();

            // Try to fetch the customer's phone number if we have a customer ID and no phone number was provided
            if (actualCustomerId && !phoneNumber) {
                fetchCustomerPhoneNumber(actualCustomerId);
            }

            // Set connection timeout - if socket isn't connected in 8 seconds, show a warning
            connectionTimeout.current = setTimeout(() => {
                if (socket && !socket.connected) {
                    console.warn('Socket connection timeout');
                    if (toast.current) {
                        toast.current.show({
                            severity: 'warn',
                            summary: 'Connection Issue',
                            detail: 'Having trouble connecting to payment service. You can still proceed, but may need to refresh if payment confirmation is delayed.'
                        });
                    }
                }
            }, 8000);
        }

        // Cleanup on modal close or unmount
        return () => {
            cleanup();
        };
    }, [visible, actualCustomerId, phoneNumber]);

    // Function to fetch customer phone number from API
    const fetchCustomerPhoneNumber = async (customerId) => {
        if (!customerId) {
            console.log('No customer ID provided, skipping phone number fetch');
            return;
        }
        try {
            console.log(`Fetching phone number for customer: ${customerId}`);
            const url = `${getCustomerPhoneNumberURL}/${customerId}`;

            const response = await makeRequest(url, 'GET', {});
            if (response.success && response.data && response.data.phoneNumber) {
                const formattedPhone = formatInitialPhoneNumber(response.data.phoneNumber);
                setPaymentFormData(prev => ({
                    ...prev,
                    phone: formattedPhone
                }));
            }
        } catch (error) {
            console.error("Error fetching customer phone number:", error.message);
            // Don't show toast as this is a background enhancement
        }
    };

    const fetchFacilityPaymentDetails = async () => {
        if (!actualFacilityId) {
            console.warn('No facilityId provided, cannot fetch facility payment details');
            return;
        }
        try {
            // This is an external gateway URL (not our own backend), so it
            // must be called directly with axios - makeRequest() prepends
            // our own backend_url, which would break this absolute URL.
            const url = `${PAYMENT_API_BASE_URL}/v1/getPaymentDetails/${actualFacilityId}`;
            const response = await axios.get(url);

            console.log('Facility payment details:', response.data);
            if (response.data && response.data.mpesaDetails) {
                setFacilityData(response.data.mpesaDetails);
            } else {
                console.warn('Facility payment details not found or invalid format');
            }
        } catch (error) {
            console.error('Error fetching facility payment details:', error.message);
            if (toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Facility Info',
                    detail: 'Could not verify payment processing details. This might affect payment processing.'
                });
            }
        }
    };
    const initSocketConnection = () => {
        try {
            const socketInstance = io(SOCKET_URL, {
                transports: ['websocket'],
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionAttempts: 5,
                timeout: 10000
            });

            setSocket(socketInstance);

            // Setup socket event listeners
            socketInstance.on('connect', () => {
                // Register this client with the server using the unique identifier
                socketInstance.emit('register', { uniqueIdentifier: uniqueIdentifier.current });

                // Setup ping interval to keep connection alive
                pingInterval.current = setInterval(() => {
                    socketInstance.emit('ping');
                }, 30000);
            });

            socketInstance.on('disconnect', () => {
                console.log('Socket disconnected');
                clearInterval(pingInterval.current);
            });

            socketInstance.on('connect_error', (error) => {
                console.error('Socket connection error:', error);
            });

            socketInstance.on('error', (error) => {
                console.error('Socket error:', error);
                if (toast.current) {
                    toast.current.show({
                        severity: 'warn',
                        summary: 'Connection Issue',
                        detail: 'Having trouble connecting to payment service. Payment status updates may be delayed.'
                    });
                }
            });

            socketInstance.on('registered', (data) => {
                console.log('Registration confirmed:', data);
            });

            socketInstance.on('pong', () => {
                console.log('Pong received - connection active');
            });

            // Listen for STK push initiation confirmation
            socketInstance.on('stkPushInitiated', (data) => {
                setStatus('pending');
                if (toast.current) {
                    toast.current.show({
                        severity: 'info',
                        summary: 'Payment Initiated',
                        detail: data.message || 'Please check your phone to complete the payment'
                    });
                }
            });

            // Listen for transaction status updates
            socketInstance.on('transactionStatus', (data) => {
                console.log('Transaction status update:', data);

                if (data.status === 'success') {
                    setStatus('completed');
                    if (toast.current) {
                        toast.current.show({
                            severity: 'success',
                            summary: 'Payment Successful',
                            detail: `Your payment of KSH ${data.data?.amount || paymentFormData.amount} has been received. Receipt: ${data.data?.receiptNumber || ''}`
                        });
                    }

                    if (onSuccess) {
                        onSuccess({
                            amount: data.data?.amount || paymentFormData.amount,
                            transactionCode: data.data?.receiptNumber,
                            accountNumber
                        });
                    }
                } else {
                    setStatus('failed');
                    if (toast.current) {
                        toast.current.show({
                            severity: 'error',
                            summary: 'Payment Failed',
                            detail: data.data?.message || 'Payment processing failed. Please try again.'
                        });
                    }

                    if (onError) {
                        onError(new Error(data.data?.message || 'Payment failed'));
                    }
                }

                setLoading(false);
            });
        } catch (error) {
            console.error('Failed to initialize socket:', error);
        }
    };

    // Clean up socket and intervals
    const cleanup = () => {
        if (socket) {
            console.log('Cleaning up socket connection');
            clearInterval(pingInterval.current);
            socket.disconnect();
            setSocket(null);
        }

        if (connectionTimeout.current) {
            clearTimeout(connectionTimeout.current);
        }
    };

    const formatPhoneNumber = (phone) => formatInitialPhoneNumber(phone);

    const validatePhoneNumber = (phone) => /^254\d{9}$/.test(phone);

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage('');
        console.log("Payment submission started");

        try {
            // Check required fields before proceeding
            console.log("Validating required fields...");

            if (!actualFacilityId) {
                throw new Error('Missing facility ID for payment processing');
            }

            // FIXED: Make customer ID optional for the payment
            // Instead of throwing error, create a dummy ID for the payment
            const paymentCustomerId = actualCustomerId || 'guest-' + uniqueIdentifier.current;

            if (!accountNumber) {
                throw new Error('Missing account number for payment processing');
            }

            const formattedPhone = formatPhoneNumber(paymentFormData.phone);
            console.log("Formatted phone number:", formattedPhone);

            if (!validatePhoneNumber(formattedPhone)) {
                throw new Error('Please enter a valid Safaricom number (e.g., 0712345678)');
            }

            // Always use our own known-correct facility ID - facilityData
            // comes from the gateway's own getPaymentDetails response and is
            // only used to warn if payment processing looks unconfigured; it
            // must never override the actual ID used to route the payment,
            // or a facilityData.facility referring to the wrong record
            // silently sends the STK push to the wrong facility, which the
            // gateway then reports back as "Facility not found".
            const payload = {
                phone: formattedPhone,
                amount: parseInt(paymentFormData.amount),
                accountNumber,
                facilityId: actualFacilityId,
                customerId: paymentCustomerId,
                uniqueIdentifier: uniqueIdentifier.current
            };

            console.log("Constructed payment payload:", payload);

            // Check socket connection but proceed even if not connected
            if (!socket || !socket.connected) {
                console.warn('No active socket connection detected');
                if (toast.current) {
                    toast.current.show({
                        severity: 'warn',
                        summary: 'Connection Status',
                        detail: 'Payment status updates may be delayed. Keep this window open after completing payment on your phone.'
                    });
                }
            } else {
                console.log('Socket connection is active');
            }

            // FIXED: Use the correct API URL
            const url = `${PAYMENT_API_BASE_URL}/v1/stkpush`;
            console.log("Sending POST request to:", url);

            const response = await axios.post(url, payload);

            console.log("Payment initiation response:", response.data);

            if (response.data && response.data.success) {
                console.log("Payment initiated successfully");
                setStatus('initiated');

                if (toast.current) {
                    toast.current.show({
                        severity: 'info',
                        summary: 'Processing',
                        detail: 'Initiating payment. Please check your phone for the M-Pesa prompt.'
                    });
                }
            } else {
                const errorMsg = response.data?.error || 'Failed to initiate payment';
                console.error("Payment initiation failed:", errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("Caught payment error:", error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to process payment';

            setErrorMessage(errorMessage);

            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Payment Error',
                    detail: errorMessage
                });
            }

            setStatus('failed');
            setLoading(false);

            if (onError) {
                console.log("Calling onError callback with error:", error);
                onError(error);
            }
        }
    };


    // Get status-based UI elements
    const getStatusUI = () => {
        switch (status) {
            case 'initiated':
                return (
                    <div className="alert alert-info">
                        <div className="d-flex align-items-center">
                            <i className="pi pi-spin pi-spinner me-2"></i>
                            <span>Payment request sent. Waiting for M-Pesa prompt...</span>
                        </div>
                    </div>
                );
            case 'pending':
                return (
                    <div className="alert alert-info">
                        <div className="d-flex align-items-center">
                            <i className="pi pi-mobile me-2"></i>
                            <span>STK push sent to your phone. Please complete the payment on your phone.</span>
                        </div>
                    </div>
                );
            case 'completed':
                return (
                    <div className="alert alert-success">
                        <div className="d-flex align-items-center">
                            <i className="pi pi-check-circle me-2"></i>
                            <span>Payment completed successfully! Thank you.</span>
                        </div>
                    </div>
                );
            case 'failed':
                return (
                    <div className="alert alert-danger">
                        <div className="d-flex align-items-center">
                            <i className="pi pi-times-circle me-2"></i>
                            <span>{errorMessage || 'Payment failed. Please try again.'}</span>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    // Handle dialog close - confirm if payment is in progress
    const handleHide = () => {
        if (status === 'pending' || status === 'initiated') {
            if (window.confirm('Payment is in progress. Are you sure you want to close this window?')) {
                onHide();
            }
        } else {
            onHide();
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={handleHide}
                header="Make Payment"
                modal
                className="payment-modal"
                style={{ width: '90%', maxWidth: '450px' }} // Adjusted width
                closable={status !== 'pending' && status !== 'initiated'}
            >
                <form onSubmit={handlePaymentSubmit}>
                    <div className="form-group mb-4">
                        <label htmlFor="phone">M-Pesa Phone Number*</label>
                        <input
                            type="tel"
                            className="form-control"
                            id="phone"
                            name="phone"
                            value={paymentFormData.phone}
                            onChange={(e) => setPaymentFormData(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="e.g., 0712345678"
                            disabled={loading || status === 'pending' || status === 'completed'}
                            required
                        />
                        <small className="text-muted">Format: 07XXXXXXXX or 254XXXXXXXXX</small>
                    </div>

                    <div className="form-group mb-4">
                        <label htmlFor="amount">Amount (KSH)*</label>
                        <input
                            type="number"
                            className="form-control"
                            id="amount"
                            name="amount"
                            value={paymentFormData.amount}
                            onChange={(e) => setPaymentFormData(prev => ({ ...prev, amount: e.target.value }))}
                            disabled={loading || status === 'pending' || status === 'completed'}
                            required
                        />
                    </div>

                    {getStatusUI()}

                    <div className="text-end mt-4">
                        {status === 'completed' ? (
                            <Button
                                label="Close"
                                icon="pi pi-check"
                                type="button"
                                onClick={onHide}
                                className="btn btn-success"
                            />
                        ) : status === 'failed' ? (
                            <Button
                                label="Try Again"
                                icon="pi pi-refresh"
                                type="submit"
                                className="btn btn-primary"
                                onClick={() => setStatus(null)}
                            />
                        ) : (
                            <Button
                                label={loading ? 'Processing...' : status === 'pending' ? 'Waiting for payment...' : 'Pay Now'}
                                icon={loading ? "pi pi-spin pi-spinner" : status === 'pending' ? "pi pi-clock" : "pi pi-check"}
                                type="submit"
                                disabled={loading || status === 'pending' || status === 'initiated'}
                                className="btn btn-primary"
                            />
                        )}
                    </div>

                    {(status === 'pending' || status === 'initiated') && (
                        <div className="text-center mt-3">
                            <p className="text-sm text-muted">
                                Please keep this window open while completing the payment on your phone.
                            </p>
                        </div>
                    )}

                    <div className="text-center mt-3">
                        <p className="text-xs text-muted">
                            Invoice: {accountNumber} | Transaction ID: {uniqueIdentifier.current.substring(0, 8)}...
                        </p>
                    </div>
                </form>
            </Dialog>
        </>
    );
};

export default PaymentModal;